import { YES_NO, applicantSection, feeInfo, otherAttachments, replySection } from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B11. Wniosek o realizację zadania publicznego w ramach inicjatywy lokalnej (art. 19b–19h ustawy z 24.04.2003 r.
 * o działalności pożytku publicznego i o wolontariacie) albo zgłoszenie projektu do budżetu obywatelskiego (art. 5a
 * ust. 3–7 ustawy o samorządzie gminnym) — dwie procedury z katalogu jako warianty. Tryb, kryteria i wymogi formalne
 * ustala uchwała rady gminy, więc pola i załączniki do potwierdzenia z wydziałem. Brak wzorów ogólnopolskich; układ
 * jak wzory pobrane 2026-10-22:
 * - inicjatywa lokalna — Mysłowice (nazwa, termin i kwota zadania, dane organizacji / grupy, opis, miejsce, cel,
 *   harmonogram, rezultaty, kosztorys ze środkami gminy i własnymi, praca społeczna, wkład rzeczowy, koszty eksploatacji,
 *   partnerzy i zasoby) — https://www.myslowice.pl/brepo/panel_repo/2025/05/14/cfctnn/inicjatywa-lokalna-wniosek.pdf,
 * - budżet obywatelski — Ruda Śląska 2027 (nazwa, opisy, uzasadnienie, ogólnodostępność, lokalizacja, szacunkowy
 *   kosztorys, lista poparcia, oświadczenia projektodawcy) — https://cdnserverbo.org/gminy/rudaslaska/download/14/Formularz_zgłoszenia.pdf?W5NS.
 * Bez sekcji pełnomocnika: grupę mieszkańców reprezentuje wnioskodawca (lista członków z upoważnieniem), organizację —
 * osoba uprawniona według rejestru (odpis lub pełnomocnictwo jako załącznik), a projektodawcą BO jest mieszkaniec.
 */
const LOCAL: EbiCondition = { field: "rodzajWniosku", equals: "inicjatywa" };
const BUDGET: EbiCondition = { field: "rodzajWniosku", equals: "budzet" };
const GROUP: EbiCondition = { all: [LOCAL, { field: "ilWnioskodawca", equals: "grupa" }] };
const NGO: EbiCondition = { all: [LOCAL, { field: "ilWnioskodawca", equals: "organizacja" }] };

const money = (code: string, label: string, required = false) =>
  ({ code, type: "number", label, required, decimals: 2, min: 0, max: 100000000, width: "third" }) as const;

const B11: IEbiFormDefinition = {
  code: "B11",
  version: 1,
  title: "Inicjatywa lokalna albo projekt do budżetu obywatelskiego",
  category: EEbiFormCategory.Community,
  office: EEbiFormOffice.Municipality,
  description: "Wniosek mieszkańców o wspólną z gminą realizację zadania (inicjatywa lokalna) albo zgłoszenie projektu do budżetu obywatelskiego.",
  keywords: ["inicjatywa lokalna", "budżet obywatelski", "budżet partycypacyjny", "projekt", "mieszkańcy", "organizacja pozarządowa", "wolontariat"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (tryb i kryteria — uchwała rady gminy)",
  legalBasis:
    "art. 19b–19h ustawy z dnia 24 kwietnia 2003 r. o działalności pożytku publicznego i o wolontariacie; art. 5a ust. 3–7 ustawy z dnia 8 marca 1990 r. o samorządzie gminnym; uchwała rady gminy",
  request:
    "Wnoszę o realizację zadania publicznego w ramach inicjatywy lokalnej albo zgłaszam projekt do budżetu obywatelskiego — zgodnie z rodzajem wniosku wskazanym poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty.",
  deadlineLabel: "wg uchwały",
  deadline:
    "Inicjatywa lokalna — według trybu z uchwały rady gminy (zawiadomienie o sposobie załatwienia wniosku zwykle do miesiąca); budżet obywatelski — w terminach naboru i weryfikacji z uchwały rady gminy.",
  variantField: "rodzajWniosku",
  variants: {
    inicjatywa: { title: "Wniosek o realizację zadania publicznego w ramach inicjatywy lokalnej", documentType: "Wniosek" },
    budzet: { title: "Zgłoszenie projektu do budżetu obywatelskiego", documentType: "Pismo" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj wniosku",
      fields: [
        {
          code: "rodzajWniosku",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            {
              value: "inicjatywa",
              label: "wniosek o realizację zadania publicznego w ramach inicjatywy lokalnej — mieszkańcy wspólnie z gminą, z wkładem pracy społecznej, rzeczowym lub finansowym",
            },
            { value: "budzet", label: "zgłoszenie projektu do budżetu obywatelskiego" },
          ],
        },
      ],
    },
    applicantSection("Wnioskodawca (projektodawca)"),
    {
      code: "wnioskodawcaIl",
      title: "Wnioskodawca inicjatywy lokalnej",
      visibleWhen: LOCAL,
      fields: [
        {
          code: "ilWnioskodawca",
          type: "radio",
          label: "Wniosek składa",
          required: true,
          defaultValue: "grupa",
          options: [
            { value: "grupa", label: "grupa inicjatywna mieszkańców (bezpośrednio)" },
            { value: "organizacja", label: "organizacja pozarządowa albo inny podmiot w imieniu mieszkańców" },
          ],
        },
        { code: "orgNazwa", type: "text", label: "Pełna nazwa organizacji", required: true, visibleWhen: NGO, maxLength: 300 },
        { code: "orgRejestr", type: "text", label: "Numer w Krajowym Rejestrze Sądowym lub w innym rejestrze", required: true, visibleWhen: NGO, width: "half", maxLength: 50 },
        { code: "orgNip", type: "nip", label: "NIP", visibleWhen: NGO, width: "half" },
        { code: "orgRegon", type: "text", label: "REGON", visibleWhen: NGO, width: "half", maxLength: 14 },
        { code: "orgAdres", type: "text", label: "Adres siedziby organizacji", required: true, visibleWhen: NGO, maxLength: 300 },
        {
          code: "osobyUpowaznione",
          type: "text",
          label: "Imiona i nazwiska osób upoważnionych do podpisania umowy o wykonanie inicjatywy lokalnej",
          required: true,
          visibleWhen: LOCAL,
          maxLength: 500,
        },
      ],
    },
    {
      code: "zadanie",
      title: "Opis zadania",
      visibleWhen: LOCAL,
      fields: [
        { code: "nazwaZadania", type: "text", label: "Nazwa zadania", required: true, maxLength: 400 },
        { code: "terminZadania", type: "text", label: "Termin realizacji", required: true, width: "half", maxLength: 100, placeholder: "np. maj — wrzesień 2027 r." },
        { ...money("kosztCalkowity", "Całkowity koszt zadania brutto [zł]", true), width: "half" },
        {
          code: "obszar",
          type: "select",
          label: "Obszar zadania",
          required: true,
          options: [
            { value: "infrastruktura", label: "infrastruktura gminna (np. drogi, kanalizacja, wodociąg, budynki i obiekty gminy)" },
            { value: "kultura", label: "kultura, sztuka, ochrona dóbr kultury i dziedzictwa narodowego" },
            { value: "edukacja", label: "edukacja, oświata i wychowanie" },
            { value: "sport", label: "kultura fizyczna, sport i turystyka" },
            { value: "przyroda", label: "ochrona przyrody i środowiska" },
            { value: "porzadek", label: "porządek i bezpieczeństwo publiczne" },
            { value: "rewitalizacja", label: "rewitalizacja" },
            { value: "inny", label: "inny obszar wskazany w uchwale rady gminy" },
          ],
        },
        { code: "opisZadania", type: "textarea", label: "Nazwa i opis inicjatywy", required: true, maxLength: 5000 },
        { code: "miejsceZadania", type: "textarea", label: "Miejsce wykonywania zadania", required: true, maxLength: 1000 },
        { code: "celZadania", type: "textarea", label: "Cel zadania oraz planowana liczba uczestników inicjatywy lokalnej", required: true, maxLength: 3000 },
        {
          code: "harmonogram",
          type: "textarea",
          label: "Wstępny harmonogram planowanych działań (z terminami rozpoczęcia i zakończenia)",
          required: true,
          maxLength: 3000,
        },
        {
          code: "rezultaty",
          type: "textarea",
          label: "Zakładane rezultaty realizacji zadania wraz z określeniem liczby osób, którym służyć będzie inicjatywa lokalna",
          required: true,
          maxLength: 3000,
        },
        { code: "zadanieInwestycyjne", type: "radio", label: "Czy zadanie ma charakter inwestycyjny (budowa, rozbudowa, remont)?", required: true, options: YES_NO },
      ],
    },
    {
      code: "koszty",
      title: "Kalkulacja przewidywanych kosztów",
      visibleWhen: LOCAL,
      fields: [
        {
          code: "kosztorys",
          type: "repeat",
          label: "Kosztorys ze względu na rodzaj kosztów (bez wkładu rzeczowego i pracy społecznej)",
          itemLabel: "Pozycja kosztorysu",
          required: true,
          min: 1,
          max: 50,
          fields: [
            { code: "rodzajKosztu", type: "text", label: "Rodzaj kosztu i sposób jego kalkulacji", required: true, maxLength: 300 },
            money("kosztPozycji", "Koszt całkowity [zł]", true),
            money("srodkiGminy", "W tym ze środków gminy [zł]"),
            money("srodkiWlasne", "W tym ze środków własnych [zł]"),
          ],
        },
        {
          code: "pracaSpoleczna",
          type: "textarea",
          label: "Opis i godzinowy wymiar zadań wykonywanych przez wolontariuszy (praca społeczna)",
          maxLength: 3000,
        },
        { code: "godzinyPracy", type: "number", label: "Łączny wymiar pracy społecznej [godz.]", min: 0, max: 1000000, width: "half" },
        { code: "wkladRzeczowy", type: "textarea", label: "Opis i wartość wkładu rzeczowego", maxLength: 3000 },
        {
          code: "kosztyEksploatacji",
          type: "textarea",
          label: "Przypuszczalne koszty eksploatacji inicjatywy lokalnej ponoszone przez gminę po jej zrealizowaniu",
          maxLength: 2000,
        },
      ],
    },
    {
      code: "inneIl",
      title: "Inne informacje dotyczące zadania",
      visibleWhen: LOCAL,
      fields: [
        { code: "partnerzy", type: "textarea", label: "Partnerzy biorący udział w realizacji zadania", maxLength: 2000 },
        {
          code: "zasobyKadrowe",
          type: "textarea",
          label: "Zasoby kadrowe przewidywane do wykorzystania (kwalifikacje osób i wolontariuszy)",
          maxLength: 2000,
        },
        { code: "zasobyRzeczowe", type: "textarea", label: "Zasoby rzeczowe (lokalowe, sprzętowe, inne) ważne dla realizacji zadania", maxLength: 2000 },
        { code: "doswiadczenie", type: "textarea", label: "Dotychczasowe doświadczenia w realizacji zadań podobnego rodzaju", maxLength: 2000 },
      ],
    },
    {
      code: "projekt",
      title: "Projekt do budżetu obywatelskiego",
      visibleWhen: BUDGET,
      fields: [
        { code: "nazwaProjektu", type: "text", label: "Nazwa projektu", required: true, maxLength: 400 },
        {
          code: "pulaProjektu",
          type: "text",
          label: "Pula lub kategoria projektu",
          hint: "Jeśli regulamin budżetu je wyróżnia (np. projekt ogólnogminny, osiedlowy, zadanie duże lub małe).",
          maxLength: 200,
        },
        {
          code: "opisSkrocony",
          type: "textarea",
          label: "Skrócony opis projektu",
          required: true,
          maxLength: 300,
          hint: "Tekst wykorzystywany w głosowaniu i promocji projektów.",
        },
        { code: "opisSzczegolowy", type: "textarea", label: "Szczegółowy opis projektu — co ma zostać wykonane", required: true, maxLength: 5000 },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie realizacji projektu — na jakie potrzeby mieszkańców odpowiada",
          required: true,
          maxLength: 2000,
        },
        {
          code: "ogolnodostepnosc",
          type: "textarea",
          label: "Ogólnodostępność — zasady korzystania z efektów projektu przez mieszkańców",
          required: true,
          maxLength: 2000,
          hint: "Np. godziny otwarcia, zasady korzystania, warunki uczestnictwa.",
        },
        {
          code: "obszarProjektu",
          type: "radio",
          label: "Obszar realizacji projektu",
          required: true,
          options: [
            { value: "jednostka", label: "jedno osiedle, dzielnica lub sołectwo" },
            { value: "kilka", label: "kilka osiedli, dzielnic lub sołectw" },
            { value: "gmina", label: "cała gmina" },
          ],
        },
        {
          code: "nazwyJednostek",
          type: "text",
          label: "Nazwa osiedla, dzielnicy lub sołectwa",
          required: true,
          visibleWhen: { all: [BUDGET, { field: "obszarProjektu", in: ["jednostka", "kilka"] }] },
          maxLength: 300,
        },
        {
          code: "miejsceProjektu",
          type: "textarea",
          label: "Miejsce realizacji — adres, numer działki lub dokładne określenie miejsca",
          required: true,
          maxLength: 1000,
          hint: "Teren musi należeć do gminy i być ogólnodostępny — szczegóły w regulaminie budżetu obywatelskiego.",
        },
        {
          code: "kosztorysBo",
          type: "repeat",
          label: "Szacunkowy kosztorys",
          itemLabel: "Składowa projektu",
          required: true,
          min: 1,
          max: 50,
          fields: [
            { code: "skladowa", type: "text", label: "Składowa projektu (np. projekt, materiały, robocizna, zakup sprzętu)", required: true, maxLength: 300 },
            money("koszt", "Koszt [zł]", true),
            { code: "zrodloKalkulacji", type: "text", label: "Źródło danych do kalkulacji kosztu", maxLength: 300, width: "twoThirds" },
          ],
        },
        {
          code: "projektodawcaNiepelnoletni",
          type: "radio",
          label: "Czy projektodawca jest osobą niepełnoletnią?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "oswZamieszkanie",
          type: "declaration",
          label: "Oświadczam, że jestem mieszkańcem gminy (przebywam na jej terenie z zamiarem stałego pobytu).",
          required: true,
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenie",
      fields: [
        {
          code: "oswPrawdziwosc",
          type: "declaration",
          label:
            "Oświadczam, że wszystkie informacje podane we wniosku (formularzu) oraz w załącznikach, w tym na liście poparcia lub liście członków grupy, są zgodne z aktualnym stanem prawnym i faktycznym.",
          required: true,
        },
        feeInfo(
          "infoUchwala",
          "Tryb oceny, kryteria, wymagana liczba podpisów poparcia i terminy naboru określa uchwała rady gminy — sprawdź ją przed złożeniem wniosku.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "listaCzlonkow",
      label: "Lista członków grupy inicjatywnej (imiona i nazwiska, adresy, podpisy) z upoważnieniem reprezentantów",
      hint: "Skan podpisanej listy.",
      visibleWhen: GROUP,
      required: true,
    },
    {
      code: "odpisRejestru",
      label: "Odpis z KRS lub innego rejestru albo pełnomocnictwo do działania w imieniu organizacji",
      hint: "Gdy nie wynika z rejestru dostępnego publicznie (praktyka urzędów).",
      visibleWhen: NGO,
    },
    {
      code: "dokumentacjaInwestycji",
      label: "Kosztorys inwestorski, projekt, pozwolenie na budowę lub zgłoszenie, mapa z numerem działki",
      hint: "Nieobowiązkowo — zwykle punktowane w ocenie wniosku.",
      visibleWhen: { all: [LOCAL, { field: "zadanieInwestycyjne", equals: "tak" }] },
      multiple: true,
    },
    {
      code: "wkladWlasny",
      label: "Oświadczenie o zgromadzonych środkach własnych, umowy partnerskie, informacje o innych źródłach finansowania",
      hint: "Nieobowiązkowo (praktyka urzędów).",
      visibleWhen: LOCAL,
      multiple: true,
    },
    {
      code: "listaPoparcia",
      label: "Lista poparcia projektu podpisana przez mieszkańców",
      hint: "Liczba podpisów według uchwały rady gminy — nie więcej niż 0,1% mieszkańców obszaru (art. 5a ust. 7 pkt 2 ustawy o samorządzie gminnym).",
      visibleWhen: BUDGET,
      required: true,
    },
    {
      code: "zgodaOpiekuna",
      label: "Zgoda rodzica lub opiekuna prawnego na zgłoszenie projektu",
      visibleWhen: { all: [BUDGET, { field: "projektodawcaNiepelnoletni", equals: "tak" }] },
      required: true,
    },
    {
      code: "dokumentacjaProjektu",
      label: "Dokumentacja pomocnicza: zgody właściciela lub zarządcy terenu, mapy, szkice, zdjęcia, wizualizacje",
      hint: "Nieobowiązkowo.",
      visibleWhen: BUDGET,
      multiple: true,
    },
    otherAttachments(),
  ],
};

export default B11;
